"use client";

import { useEffect, useMemo, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";

export const dynamic = "force-dynamic";

const DEFAULT_ADMIN_WALLET = "0x3C2F69Ca6f18c00bb0EBcBa93Fd8252110A3992F";

const ROLE_LABELS: Record<string, string> = {
  SALESPERSON: "Platform Salesperson",
  IT_PERSON: "IT Technician",
  BOOKKEEPER: "Bookkeeper",
};

const API_URL = "http://localhost:3000/api/platform-role-accounts";
const REVENUE_DISTRIBUTOR_CONTRACT = process.env.NEXT_PUBLIC_PLATFORM_CONTRACT_ADDRESS ?? "";

type PlatformRoleAccount = {
  role: string;
  wallet: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  user?: {
    id: number;
    wallet_address: string;
    platform_role: string;
    email: string;
    name?: string;
    phone_number?: string;
  } | null;
};

export default function PlatformRoleWalletsPage() {
  const { getAccessToken } = usePrivy();
  const [accounts, setAccounts] = useState<PlatformRoleAccount[]>([]);
  const [adminWallet, setAdminWallet] = useState(DEFAULT_ADMIN_WALLET);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const roleOrder = useMemo(
    () => ["SALESPERSON", "IT_PERSON", "BOOKKEEPER"],
    []
  );

  const fetchAccounts = async () => {
    try {
      setError(null);
      const response = await fetch(API_URL, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch platform role accounts.");
      }

      const payload = await response.json();
      setAccounts(payload.accounts ?? []);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Unable to load platform role accounts."
      );
    }
  };

  useEffect(() => {
    void fetchAccounts();
  }, []);

  const findRoleAccount = (role: string) => {
    const found = accounts.find((account) => account.role === role);
    return {
      wallet: found?.wallet ?? found?.user?.wallet_address ?? DEFAULT_ADMIN_WALLET,
      name: found?.name ?? found?.user?.name ?? "",
      email: found?.email ?? found?.user?.email ?? "",
      phoneNumber: found?.phoneNumber ?? found?.user?.phone_number ?? "",
    };
  };

  const updateAccountField = (
    role: string,
    field: "wallet" | "name" | "email" | "phoneNumber",
    val: string
  ) => {
    setAccounts((current) => {
      const exists = current.some((acc) => acc.role === role);
      if (!exists) {
        return [
          ...current,
          {
            role,
            wallet: field === "wallet" ? val : "",
            name: field === "name" ? val : "",
            email: field === "email" ? val : "",
            phoneNumber: field === "phoneNumber" ? val : "",
          },
        ];
      }
      return current.map((acc) =>
        acc.role === role ? { ...acc, [field]: val } : acc
      );
    });
  };

  const authHeaders = async () => {
    const token = await getAccessToken();
    if (!token) throw new Error("Sign in again to continue.");
    return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
  };

  const handleInvite = async (role: string) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`${API_URL}/${role}/invite`, {
        method: "POST",
        headers: await authHeaders(),
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error ?? "Unable to send invitation.");
      }

      setSuccess(
        payload.emailResult?.sent
          ? `Invitation emailed to the ${ROLE_LABELS[role]}.`
          : `Invitation saved. Email is not configured; share this link: ${payload.emailResult?.inviteLink ?? ""}`
      );
    } catch (inviteError) {
      setError(inviteError instanceof Error ? inviteError.message : "Unable to send invitation.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (role: string) => {
    const roleData = findRoleAccount(role);
    const usesPrivyWallet = role === "SALESPERSON" || role === "BOOKKEEPER";
    const hasRealEmail = roleData.email.trim() !== "" && !roleData.email.endsWith(".local");
    const isDefaultWallet =
      !roleData.wallet || roleData.wallet.toLowerCase() === DEFAULT_ADMIN_WALLET.toLowerCase();
    // A blank wallet tells the backend to create a Privy wallet from the email.
    const walletAddress =
      usesPrivyWallet && hasRealEmail && isDefaultWallet
        ? ""
        : roleData.wallet || DEFAULT_ADMIN_WALLET;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`${API_URL}/${role}`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({
          walletAddress,
          name: roleData.name,
          email: roleData.email,
          phoneNumber: roleData.phoneNumber,
        }),
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error ?? "Unable to save role account.");
      }

      setSuccess(`${ROLE_LABELS[role]} details saved successfully.`);
      await fetchAccounts();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Unable to save account details."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDefaultReset = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`${API_URL}/ensure-defaults`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ adminWallet }),
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error ?? "Unable to reset default wallets.");
      }

      setSuccess("Default platform-role wallets restored to the admin wallet.");
      await fetchAccounts();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Unable to reset default wallet assignments."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="mx-auto max-w-6xl px-4 py-12 md:px-8 md:py-16">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-4xl font-bold md:text-6xl">Platform Role Accounts</h1>
            <p className="mt-2 text-lg text-slate-300">
              Manage the Name, Email address, Telephone number, and Wallet Address for platform Salesperson, IT Technician, and Bookkeeper roles.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDefaultReset}
            disabled={saving}
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving..." : "Use Admin as default"}
          </button>
        </div>
        
        <div className="mb-6 rounded-2xl border border-blue-500/40 bg-blue-500/10 p-4 text-sm text-blue-200">
          The Admin wallet is assigned to the Platform Salesperson, IT Technician, and Bookkeeper roles by default.
          Those roles will continue using the Admin wallet until the Admin changes an individual role wallet.
          <div className="mt-2 break-all font-mono text-xs text-amber-300">RevenueDistributor: {REVENUE_DISTRIBUTOR_CONTRACT}</div>
        </div>

        <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900 p-4">
          <label className="mb-2 block text-sm font-medium text-slate-300">Default Admin Wallet</label>
          <input
            value={adminWallet}
            onChange={(event) => setAdminWallet(event.target.value.trim())}
            className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-blue-500 font-mono"
            placeholder="0x..."
          />
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-xl border border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
            {success}
          </div>
        )}

        <div className="space-y-6">
          {roleOrder.map((role) => {
            const data = findRoleAccount(role);
            const label = ROLE_LABELS[role] ?? role;
            const usesAdminWallet = data.wallet.toLowerCase() === DEFAULT_ADMIN_WALLET.toLowerCase();
            const walletStatus = usesAdminWallet ? "Admin wallet assigned" : "Dedicated wallet assigned";

            return (
              <div key={role} className="rounded-3xl border border-slate-800 bg-slate-900 p-6 md:p-8">
                <div className="mb-6 flex items-center justify-between gap-3 border-b border-slate-800 pb-4">
                  <div>
                    <h2 className="text-2xl font-semibold text-white">{label}</h2>
                    <p className="text-sm text-slate-400 mt-1">Core Platform Participant Role</p>
                    <span className={`mt-3 inline-block rounded-full px-3 py-1 text-xs font-semibold ${usesAdminWallet ? "bg-blue-500/20 text-blue-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                      {walletStatus}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSave(role)}
                    disabled={saving}
                    className="rounded-xl border border-blue-500/40 bg-blue-500/10 px-5 py-2.5 text-sm font-semibold text-blue-200 hover:border-blue-400 transition disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save Role Info"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInvite(role)}
                    disabled={saving}
                    className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-5 py-2.5 text-sm font-semibold text-emerald-200 hover:border-emerald-400 transition disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Send Invitation
                  </button>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-300">
                      Full Name
                    </label>
                    <input
                      value={data.name}
                      onChange={(event) =>
                        updateAccountField(role, "name", event.target.value)
                      }
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
                      placeholder="e.g. Jane Doe"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-300">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={data.email}
                      onChange={(event) =>
                        updateAccountField(role, "email", event.target.value)
                      }
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
                      placeholder="e.g. bookkeeper@agpsira.local"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-300">
                      Telephone Number
                    </label>
                    <input
                      type="tel"
                      value={data.phoneNumber}
                      onChange={(event) =>
                        updateAccountField(role, "phoneNumber", event.target.value)
                      }
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
                      placeholder="e.g. (303) 555-0123"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-300">
                      Assigned Wallet Address
                    </label>
                    {(role === "SALESPERSON" || role === "BOOKKEEPER") && (
                      <p className="mb-1.5 text-xs text-slate-500">
                        Enter an email and save while this still shows the Admin wallet: a Privy wallet is created for that email automatically. The person signs in with the same email to use it.
                      </p>
                    )}
                    <input
                      value={data.wallet}
                      onChange={(event) =>
                        updateAccountField(role, "wallet", event.target.value)
                      }
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-blue-500 font-mono text-xs"
                      placeholder="0x..."
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}
